<template>
  <div class="scroll_flex_container retail_group_popup">
    <div class="column_center_start retail_group_info">
      <!-- 有圖片時：sticky 吸頂縮小，名稱疊在底部漸層 -->
      <div
        v-if="groupImage && !isChildDetailOpen"
        ref="groupImageWrapper"
        class="sticky z-10 w-full aspect-square bg-white retail_group_img relative"
        :style="imageStickyStyle"
      >
        <div
          ref="groupImageCarousel"
          class="retail_group_image_carousel"
          @touchstart.passive="resetImageAutoPlay"
          @mousedown="resetImageAutoPlay"
          @scroll.passive="handleGroupImageScroll"
        >
          <div
            v-for="(image, imageIndex) in carouselImages"
            :key="`${image.url}-${imageIndex}`"
            class="retail_group_image_slide"
          >
            <img :src="image.url" class="w-full h-full object-cover" />
          </div>
        </div>

        <div class="fixed top-2 right-2 m-2">
          <div class="row_center_center" style="background-color: #DDDDDD; border-radius: 50%; padding: 2px;"
            @click="$emit('close')">
            <text class="button-icon"
              style="color: #000; font-size: 32px; height: 32px; width: 32px">close</text>
          </div>
        </div>

        <div class="row_center_around absolute bottom-0 left-0 w-full px-4 pb-3 pt-10 bg-gradient-to-t from-black/35 to-transparent">
          <text class="text-white text-2xl font-semibold text_three_lines text-left flex-grow">
            {{ retailGroupName }}
          </text>
        </div>
      </div>

      <!-- 沒有圖片時：白底黑字 -->
      <div v-else-if="!isChildDetailOpen" class="sticky top-0 z-10 w-full h-[110px] bg-white retail_group_img relative">
        <div class="fixed top-2 right-2 m-2">
          <div class="row_center_center" style="background-color: #DDDDDD; border-radius: 50%; padding: 2px;"
            @click="$emit('close')">
            <text class="button-icon"
              style="color: #000; font-size: 32px; height: 32px; width: 32px">close</text>
          </div>
        </div>

        <div class="row_center_around w-full absolute bottom-0 left-0 px-4 pb-3">
          <text class="text-black text-2xl font-semibold text_three_lines text-left flex-grow">
            {{ retailGroupName }}
          </text>
        </div>
      </div>

      <div class="relative z-0 bg-white w-full" style="flex-grow: 1;">
        <div v-if="groupImages.length > 1" class="retail_group_image_thumbnails">
          <button
            v-for="(image, imageIndex) in groupImages"
            :key="`thumbnail-${image.url}-${imageIndex}`"
            type="button"
            class="retail_group_image_thumbnail"
            :class="{ active: selectedImageIndex === imageIndex }"
            @click="setSelectedGroupImage(imageIndex)"
          >
            <img :src="image.url" :alt="`${retailGroupName} 圖片 ${imageIndex + 1}`" />
          </button>
        </div>

        <div v-if="sanitizedGroupDescription" class="retail_group_description">
          <!-- HTML 內容渲染 -->
          <div
            v-if="isHtmlGroupDescription"
            v-html="sanitizedGroupDescription"
            class="html-description"
          ></div>

          <!-- 純文字內容渲染 -->
          <span v-else>{{ sanitizedGroupDescription }}</span>
        </div>

        <div class="column_center_start products retail_group_products">
          <div v-for="product in groupProducts" :key="product.id" class="product">
            <Product
              :product="product"
              :cartQuantity="selectedProductQuantity(product)"
              :disabled="isProductSoldOut(product)"
              @click="openProductDetail(product)"
            />
          </div>
          <div v-if="!groupProducts.length" class="empty_message">
            此組合目前沒有可販售的商品
          </div>
        </div>
      </div>
    </div>

    <ProductDetail
      v-if="showProductDetail"
      :product="selectedProduct"
      @close="closeProductDetail"
    />
    <comboDetail
      v-if="showComboDetail"
      :product="selectedProduct"
      @close="closeComboDetail"
    />
  </div>
</template>

<script>
import Product from "@/components/store/product.vue";
import productDetail from "@/components/store/productDetail.vue";
import comboDetail from "@/components/store/comboDetail.vue";
import commonUtilities from "@/utils/common.utils";
import { isHtmlContent, sanitizeHtml } from "@/utils/htmlSanitizer.utils";
import { getFrontEndName } from "@/utils/productName.utils";

export default {
  components: {
    Product,
    ProductDetail: productDetail,
    comboDetail
  },
  props: {
    group: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      showProductDetail: false,
      showComboDetail: false,
      selectedProduct: {},
      selectedImageIndex: 0,
      imageStickyTopOffset: 100,
      imageAutoPlayTimer: null,
      imageLoopResetTimer: null,
      isImageLooping: false,
      isProgrammaticImageScroll: false,
      imageScrollUnlockTimer: null
    };
  },
  computed: {
    cart() {
      return this.$store.state.cart.cart;
    },
    useDbData() {
      return this.$store.state.spreadsheet.useDbData;
    },
    retailGroupName() {
      return this.group.displayName || getFrontEndName(this.group.name);
    },
    isChildDetailOpen() {
      return this.showProductDetail || this.showComboDetail;
    },
    imageStickyStyle() {
      return { top: `-${this.imageStickyTopOffset}px` };
    },
    groupImage() {
      return this.groupImages[this.selectedImageIndex]?.url || this.groupImages[0]?.url || '';
    },
    groupImages() {
      const images = this.$store.getters['product/getProductImagesById'](this.group.id);
      if (images.length) {
        return images.map(image => ({
          ...image,
          url: image.image_url
        }));
      }

      const fallbackImage = this.$store.getters['product/getProductImageById'](this.group.id);
      return fallbackImage ? [{ url: fallbackImage }] : [];
    },
    carouselImages() {
      if (this.groupImages.length <= 1) return this.groupImages;

      return [
        { ...this.groupImages[this.groupImages.length - 1], isClone: true },
        ...this.groupImages,
        { ...this.groupImages[0], isClone: true }
      ];
    },
    sanitizedGroupDescription() {
      let rawDescription = this.$store.getters['product/getProductDescById'](this.group.id);

      if (!rawDescription) return '';

      try {
        rawDescription = rawDescription.replace(/<br><\/p>/g, '<br><br></p>');
        return sanitizeHtml(rawDescription);
      } catch (error) {
        console.warn('HTML 淨化失敗，回退到純文字模式:', error);
        return rawDescription.replace(/<[^>]*>/g, ''); // 移除所有 HTML 標籤
      }
    },
    isHtmlGroupDescription() {
      const rawDescription = this.$store.getters['product/getProductDescById'](this.group.id);
      return isHtmlContent(rawDescription);
    },
    groupProducts() {
      return this.$store.getters['spreadsheet/productsInRetailGroup'](this.group.id);
    }
  },
  mounted() {
    this.resetGroupImageCarouselPosition();
    this.startImageAutoPlay();
    this.$nextTick(() => this.updateImageStickyTop());
    window.addEventListener('resize', this.updateImageStickyTop);
  },
  unmounted() {
    this.stopImageAutoPlay();
    this.clearImageLoopResetTimer();
    this.clearImageScrollUnlockTimer();
    window.removeEventListener('resize', this.updateImageStickyTop);
  },
  watch: {
    'group.id'() {
      this.selectedImageIndex = 0;
      this.resetGroupImageCarouselPosition();
      this.startImageAutoPlay();
      this.$nextTick(() => this.updateImageStickyTop());
    },
    groupImages(images) {
      if (this.selectedImageIndex >= images.length) {
        this.selectedImageIndex = 0;
      }
      this.resetGroupImageCarouselPosition();
      this.startImageAutoPlay();
      this.$nextTick(() => this.updateImageStickyTop());
    },
    isChildDetailOpen(isOpen) {
      if (isOpen) {
        // 開啟商品/套餐詳情時暫停輪播
        this.stopImageAutoPlay();
        return;
      }
      // 關閉後圖片重新掛載，還原輪播位置與自動播放
      this.resetGroupImageCarouselPosition();
      this.startImageAutoPlay();
      this.$nextTick(() => this.updateImageStickyTop());
    }
  },
  methods: {
    updateImageStickyTop() {
      const wrapper = this.$refs.groupImageWrapper;
      if (!wrapper) return;
      const visibleHeight = 200;
      this.imageStickyTopOffset = Math.max(wrapper.clientHeight - visibleHeight, 0);
    },
    startImageAutoPlay() {
      this.stopImageAutoPlay();
      if (this.groupImages.length <= 1) return;

      this.imageAutoPlayTimer = window.setInterval(() => {
        this.showNextGroupImage();
      }, 5000);
    },
    resetImageAutoPlay() {
      if (this.groupImages.length <= 1) return;

      this.startImageAutoPlay();
    },
    stopImageAutoPlay() {
      if (!this.imageAutoPlayTimer) return;

      window.clearInterval(this.imageAutoPlayTimer);
      this.imageAutoPlayTimer = null;
    },
    clearImageLoopResetTimer() {
      if (!this.imageLoopResetTimer) return;

      window.clearTimeout(this.imageLoopResetTimer);
      this.imageLoopResetTimer = null;
    },
    clearImageScrollUnlockTimer() {
      if (!this.imageScrollUnlockTimer) return;

      window.clearTimeout(this.imageScrollUnlockTimer);
      this.imageScrollUnlockTimer = null;
    },
    lockProgrammaticImageScroll(duration = 550) {
      this.isProgrammaticImageScroll = true;
      this.clearImageScrollUnlockTimer();
      this.imageScrollUnlockTimer = window.setTimeout(() => {
        this.isProgrammaticImageScroll = false;
      }, duration);
    },
    showNextGroupImage() {
      if (this.groupImages.length <= 1) return;

      const isLastImage = this.selectedImageIndex === this.groupImages.length - 1;
      if (isLastImage) {
        this.isImageLooping = true;
        this.lockProgrammaticImageScroll();
        this.scrollGroupImageToIndex(this.groupImages.length + 1, 'smooth');
        this.clearImageLoopResetTimer();
        this.imageLoopResetTimer = window.setTimeout(() => {
          this.selectedImageIndex = 0;
          this.scrollGroupImageToIndex(1, 'auto');
          this.isImageLooping = false;
        }, 500);
        return;
      }

      const nextIndex = this.selectedImageIndex + 1;
      this.selectedImageIndex = nextIndex;
      this.lockProgrammaticImageScroll();
      this.scrollGroupImageToIndex(nextIndex + 1, 'smooth');
    },
    handleGroupImageScroll() {
      const carousel = this.$refs.groupImageCarousel;
      if (!carousel || !carousel.clientWidth) return;

      const imageIndex = Math.round(carousel.scrollLeft / carousel.clientWidth);
      if (this.groupImages.length <= 1) {
        this.selectedImageIndex = imageIndex;
        return;
      }

      if (this.isImageLooping || this.isProgrammaticImageScroll) return;

      if (imageIndex === 0) {
        this.selectedImageIndex = this.groupImages.length - 1;
        this.clearImageLoopResetTimer();
        this.imageLoopResetTimer = window.setTimeout(() => {
          this.scrollGroupImageToIndex(this.groupImages.length, 'auto');
        }, 100);
        return;
      }

      if (imageIndex === this.groupImages.length + 1) {
        this.selectedImageIndex = 0;
        this.clearImageLoopResetTimer();
        this.imageLoopResetTimer = window.setTimeout(() => {
          this.scrollGroupImageToIndex(1, 'auto');
        }, 100);
        return;
      }

      this.selectedImageIndex = imageIndex - 1;
    },
    setSelectedGroupImage(index) {
      this.resetImageAutoPlay();

      if (this.selectedImageIndex === this.groupImages.length - 1 && index === 0 && this.groupImages.length > 1) {
        this.showNextGroupImage();
        return;
      }

      if (this.selectedImageIndex === 0 && index === this.groupImages.length - 1 && this.groupImages.length > 1) {
        this.selectedImageIndex = index;
        this.isImageLooping = true;
        this.lockProgrammaticImageScroll();
        this.scrollGroupImageToIndex(0, 'smooth');
        this.clearImageLoopResetTimer();
        this.imageLoopResetTimer = window.setTimeout(() => {
          this.scrollGroupImageToIndex(this.groupImages.length, 'auto');
          this.isImageLooping = false;
        }, 500);
        return;
      }

      this.selectedImageIndex = index;
      this.lockProgrammaticImageScroll();
      this.$nextTick(() => {
        this.scrollGroupImageToIndex(this.groupImages.length > 1 ? index + 1 : index, 'smooth');
      });
    },
    resetGroupImageCarouselPosition() {
      this.$nextTick(() => {
        this.scrollGroupImageToIndex(this.groupImages.length > 1 ? this.selectedImageIndex + 1 : this.selectedImageIndex, 'auto');
      });
    },
    scrollGroupImageToIndex(index, behavior = 'smooth') {
      const carousel = this.$refs.groupImageCarousel;
      if (!carousel) return;

      carousel.scrollTo({
        left: carousel.clientWidth * index,
        behavior
      });
    },
    selectedProductQuantity(product) {
      if (!product || !this.cart || !this.cart.length) return 0;
      return this.cart.reduce((sum, item) => {
        return item?.product?.id === product.id ? sum + item.quantity : sum;
      }, 0);
    },
    isProductSoldOut(product) {
      return this.useDbData && Number(product?.stockMode) === 1 && product?.isStockSoldOut;
    },
    openProductDetail(product) {
      if (this.isProductSoldOut(product)) {
        commonUtilities.createToast('warning', '商品已售完');
        return;
      }

      this.selectedProduct = product;
      if (product.comboSubSettings !== undefined) {
        this.showComboDetail = true;
      } else {
        this.showProductDetail = true;
      }
    },
    closeProductDetail() {
      commonUtilities.closeLoading();
      this.showProductDetail = false;
      this.selectedProduct = {};
    },
    closeComboDetail() {
      commonUtilities.closeLoading();
      this.showComboDetail = false;
      this.selectedProduct = {};
    }
  }
};
</script>

<style lang="scss" scoped>
@import '@/static/css/html-content-fix.css';
.retail_group_popup {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background: #fff;
  z-index: 20;
}

.retail_group_info {
  width: 100%;
  background-color: #fff;
  overflow-y: auto;
  flex-grow: 1;
}

.retail_group_img {
  width: 100%;
  flex-shrink: 0;
  font-family: PingFang SC;
  font-weight: 500;
  font-size: 24px;
}

.retail_group_image_carousel {
  display: flex;
  width: 100%;
  aspect-ratio: 1 / 1;
  overflow-x: auto;
  overflow-y: hidden;
  scroll-snap-type: x mandatory;
  scrollbar-width: none;
  -webkit-overflow-scrolling: touch;
}

.retail_group_image_carousel::-webkit-scrollbar {
  display: none;
}

.retail_group_image_slide {
  width: 100%;
  height: 100%;
  flex: 0 0 100%;
  scroll-snap-align: start;
}

.retail_group_image_thumbnails {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  height: 70px;
  padding: 10px 12px;
  overflow-x: auto;
  background-color: #fff;
  scrollbar-width: none;
  -webkit-overflow-scrolling: touch;
}

.retail_group_image_thumbnails::-webkit-scrollbar {
  display: none;
}

.retail_group_image_thumbnail {
  width: 50px;
  height: 50px;
  padding: 0;
  border: 2px solid transparent;
  border-radius: 6px;
  overflow: hidden;
  flex: 0 0 50px;
  background-color: #f5f5f5;
}

.retail_group_image_thumbnail.active {
  border-color: #FD5001;
}

.retail_group_image_thumbnail img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.retail_group_description {
  width: 92%;
  margin: 0 auto;
  padding: 6px 0 15px;
  border-bottom: 1px solid #000;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.6;
  color: #666666;
  word-wrap: break-word;

  // 純文字內容保留換行
  > span {
    white-space: pre-wrap;
  }

  .html-description {
    line-height: 1.6;
    word-wrap: break-word;

    b, strong {
      font-weight: bold;
    }

    i, em {
      font-style: italic;
    }

    u {
      text-decoration: underline;
    }

    span {
      display: inline;
    }

    @media (max-width: 768px) {
      font-size: 13px;
    }
  }
}

.retail_group_products {
  width: 100%;
  padding: 12px 7% 120px;
}

.empty_message {
  width: 100%;
  padding: 48px 16px;
  color: #999999;
  font-size: 15px;
  text-align: center;
}
</style>
